import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';
import isMobile from 'ismobilejs';

import { useFormatter, useUtils } from '@ezbobdev/ui-commons';
import { MainContext, useTranslation } from '@ezbobdev/ui-framework';

const formatPerLocale = (
  value,
  options = {
    locale: '',
  },
) => {
  const isNumber = value !== '' && value !== null && !isNaN(Number(value));
  if (!isNumber) return value;

  const formatter = new Intl.NumberFormat(options.locale);
  const formattedValue = formatter.format(value);

  return formattedValue;
};

export const useInput = (props, widgetName) => {
  const { actions } = useContext(MainContext);

  const { maskText } = useFormatter();
  const { i18n, t } = useTranslation();

  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);

  const { configByName, getConfig } = useUtils(props, widgetName);

  const maskValueVisibility = getConfig('maskValueVisibility', false);
  const formattingOptions = maskValueVisibility ? { delimiter: '' } : getConfig(`formattingOptions`, { delimiter: '' });
  const disabled = getConfig('disabled', false);
  const readOnly = getConfig('readOnly', false);
  const autoFocus = getConfig('autoFocus', false);
  const fullWidth = getConfig('fullWidth', false);
  const variant = getConfig('variant');
  const size = getConfig('size', 'medium');
  const multiline = getConfig('multiline', false);
  const className = getConfig(`className`);
  const rawInputProps = getConfig(`inputProps`, {});
  const autoComplete = getConfig('autoComplete');
  const isRequired = getConfig('isRequired', false);
  const id = getConfig('id');
  const privacyRules = getConfig('privacyRules', { input: 'mask-user-input' });

  const value = maskValueVisibility
    ? maskText(actions.case.getCaseValue(configByName), getConfig('maskingOptions'))
    : formattingOptions.type === 'number'
      ? formatPerLocale(actions.case.getCaseValue(configByName), {
          locale: actions.settings.getUserConfigProp('locals.locale'),
        })
      : actions.case.getCaseValue(configByName);

  // TODO: props label needs to be deprecated and we need to align BO where label is being used
  const label = props.label ?? t(`${configByName}.label`);
  const helperText = i18n.exists(`${configByName}.helperText`) ? t(`${configByName}.helperText`) : undefined;

  const { allowPaste, ...inputProps } = rawInputProps;

  const showPasswordLabel = i18n.exists(`${configByName}.showPasswordLabel`)
    ? t(`${configByName}.showPasswordLabel`)
    : 'Show Password';
  const hidePasswordLabel = i18n.exists(`${configByName}.hidePasswordLabel`)
    ? t(`${configByName}.hidePasswordLabel`)
    : 'Hide Password';

  const passwordVisibilityLabel = revealPassword ? hidePasswordLabel : showPasswordLabel;

  const isMobileDevice = isMobile();

  const placeholder = useMemo(
    () =>
      props.hasOwnProperty(`placeholder`)
        ? props.placeholder
        : i18n.exists(`${configByName}.placeholder`)
          ? t(`${configByName}.placeholder`)
          : ``,
    [props.placeholder],
  );

  const toggleRevealPassword = useCallback(() => {
    setRevealPassword(!revealPassword);
  }, [revealPassword]);

  const validityState = disabled ? {} : props.validityState || actions.caseValidation.getValidity(configByName);

  const revealPasswordProps = useMemo(
    () =>
      inputProps.type === `password` && {
        callback: toggleRevealPassword,
        icon: revealPassword ? `eye` : `eye-slash`,
      },
    [inputProps.type, revealPassword],
  );

  const InputWrapperProps = useMemo(
    () => (revealPasswordProps ? { ...props, revealPassword: revealPasswordProps } : props),
    [revealPasswordProps],
  );

  // Captured Cleave instance — set by consumers via the returned `captureCleave` callback
  // when they wire it to Cleave's `onInit` prop. Used to resync Cleave's internal state on
  // blur of an empty currency/prefix field. See DCJ-6743.
  const cleaveInstanceRef = useRef(null);
  const captureCleave = useCallback((instance) => {
    cleaveInstanceRef.current = instance;
  }, []);

  const onBlur = useCallback(
    (e) => {
      setIsFocused(false);
      const displayValue = e.target ? e.target.value : e;
      setInputValue(displayValue);
      props.onBlur && props.onBlur(e);

      if (formattingOptions?.prefix && cleaveInstanceRef.current) {
        const rawValue = e?.target?.rawValue ?? '';
        if (!rawValue) cleaveInstanceRef.current.onInput('');
      }
    },
    [formattingOptions?.prefix],
  );

  const onFocus = useCallback(
    (e) => {
      setIsFocused(true);
      props.onFocus && props.onFocus(e);
    },
    [props.onFocus],
  );

  const hasValue = useCallback(() => value && `hasValue`, [value]);

  const hasValueClass = useMemo(() => hasValue(), [value]);

  const wrapperClassNames = useMemo(
    () => classnames(widgetName, className, configByName, hasValue, size),
    [className, configByName, hasValueClass, size],
  );

  const inputType = useMemo(() => {
    if (inputProps.type === 'password') return revealPassword ? `text` : `password`;
    return inputProps.type;
  }, [inputProps.type, revealPassword]);

  const onPaste = useCallback(
    (e) => {
      if (allowPaste === false) {
        e.preventDefault();
      }

      if (typeof inputProps.onPaste === 'function') {
        inputProps.onPaste(e);
      }
    },
    [allowPaste, inputProps.onPaste],
  );

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return {
    ...props,
    autoComplete,
    revealPassword,
    toggleRevealPassword,
    captureCleave,
    onBlur,
    onFocus,
    value,
    configByName,
    placeholder,
    disabled,
    validityState,
    wrapperClassNames,
    formattingOptions,
    inputProps: {
      ...inputProps,
      type: inputType,
      onPaste: onPaste,
      onInput: isMobileDevice.any ? props?.onInput : undefined,
    },
    revealPasswordProps,
    InputWrapperProps,
    isRequired,
    inputType,
    autoFocus,
    fullWidth,
    variant,
    inputValue,
    privacyRules,
    maskValueVisibility,
    multiline,
    id,
    label,
    helperText,
    passwordVisibilityLabel,
    readOnly,
    isFocused,
  };
};
